import Image from "next/image";
import React from "react";
import { buttonVariants } from "../ui/button";
import { cn, formatNoOfReviews, formatPrice } from "@/lib/utils";
import { StarIcon } from "@heroicons/react/24/solid";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import AddToCartDialog from "./add-to-cart-dialog";
import { useCart } from "@/hooks/useCart";
import { generateCartItemId } from "@/lib/utils/cart-storage";
import { ShoppingCartIcon } from "lucide-react";
import AnimatedLink from "../ui/animated-link";
import { ShieldCheckIcon, WrenchIcon } from "@heroicons/react/24/outline";

export default function ProductCard({
  size = "md",
  image,
  title,
  price,
  rating,
  noOfReviews,
  id,
  assetProtected,
  installationProtection,
  details,
}: {
  size: "sm" | "md" | "lg";
  image: string;
  title: string;
  price: number;
  rating?: number;
  noOfReviews?: number;
  id: number;
  assetProtected?: boolean;
  installationProtection?: boolean;
  details: string;
}) {
  const [open, setOpen] = React.useState(false);
  const { hasItem, getItemById } = useCart();

  // Check if this product is in cart (with default variant)
  const defaultItemId = generateCartItemId(id, {});
  const isInCart = hasItem(defaultItemId);
  const cartItem = getItemById(defaultItemId);

  return (
    <div
      key={id}
      className={cn(
        "group relative flex w-full flex-col rounded-lg p-0 shadow-lg md:overflow-hidden xl:shadow-none",
        size === "sm" ? "h-88" : size === "md" ? "h-[300px] md:h-[360px]" : "h-[360px]"
      )}
    >
      <div className='absolute top-2 right-2 z-10 flex items-center gap-1'>
        {assetProtected && (
          <div
            className='bg-custom-amber flex size-10 items-center justify-center rounded-full shadow-md'
            title='Asset Protection'
          >
            <ShieldCheckIcon className='text-custom-amber-text size-5 stroke-[1.8px]' />
          </div>
        )}
        {installationProtection && (
          <div
            className='bg-custom-amber flex size-10 items-center justify-center rounded-full shadow-md'
            title='Installation Protection'
          >
            <WrenchIcon className='text-custom-amber-text size-5 stroke-[1.8px]' />
          </div>
        )}
      </div>

      <Link
        href={`/shop/${id}`}
        title={title}
        className={cn(
          "bg-bg-muted relative w-full overflow-hidden rounded-t-lg xl:rounded-lg",
          size === "sm" ? "h-74" : size === "md" ? "h-2/3" : "h-2/3"
        )}
      >
        <Image
          src={image}
          alt={title}
          fill
          loading='lazy'
          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
          quality={75}
          title={details}
          placeholder='blur'
          blurDataURL={`data:image/svg+xml;base64,${Buffer.from(
            '<svg width="400" height="400" xmlns="http://www.w3.org/2000/svg"><rect width="400" height="400" fill="#f3f4f6"/></svg>'
          ).toString("base64")}`}
          className='size-full object-cover transition-transform duration-600 group-hover:scale-110'
        />
        {isInCart && (
          <div className='bg-bg-base absolute top-2 right-2 flex items-center gap-1 rounded-full px-2 py-1 shadow-md'>
            <CheckCircleIcon className='size-4 text-green-500' />
            <span className='text-text-title text-xs font-medium'>{cartItem?.quantity}</span>
          </div>
        )}
      </Link>
      <div className='flex flex-1 flex-col px-3 py-2 xl:px-0 xl:pt-4 xl:pb-0'>
        <div className='flex h-full flex-col justify-between'>
          <Link
            href={`/shop/${id}`}
            title={title}
            className='text-text-title w-[90%] max-w-full truncate text-base font-medium whitespace-nowrap'
          >
            {title}
          </Link>
          <div className='flex flex-col-reverse justify-between xl:flex-row xl:items-center'>
            <span className='text-text-title text-xl font-semibold'>{formatPrice(price)}</span>
            <span className='flex items-center gap-1'>
              <StarIcon className='text-link-primary size-4' />
              {rating && noOfReviews ? (
                <span className='text-text-title text-xs'>
                  {rating}
                  {` (${formatNoOfReviews(noOfReviews)} reviews)`}
                </span>
              ) : (
                <span className='invisible'>.</span>
              )}
            </span>
          </div>
          <div className='flex items-center gap-4'>
            <AnimatedLink
              as='button'
              onClick={() => setOpen(true)}
              className={cn(
                buttonVariants({ variant: "outline" }),
                "text-text-title hover:bg-btn-primary hover:text-text-title-inv dark:hover:text-text-title flex w-full flex-1 items-center justify-center gap-2 rounded-xl text-xs font-semibold md:text-sm xl:rounded-full xl:py-6"
              )}
            >
              <ShoppingCartIcon className='size-4' />
              Add to Cart
            </AnimatedLink>
          </div>
        </div>
      </div>
      <AddToCartDialog open={open} setOpen={setOpen} id={id} />
    </div>
  );
}
